import {
  Accordion,
  Group,
  Stack,
  Text,
  Textarea,
  ActionIcon,
  Card,
  Divider,
} from "@mantine/core"
import { IconMessage2, IconSend } from "@tabler/icons-react"
import { ReactionPillGroup } from "../ReactionPillGroup"
import { FeedPostCommentList } from "./FeedPostCommentList"
import { EmojiPickerDropdown } from "../../../../-components/EmojiPickerDropdown"
import { useGetAuthenticatedUser } from "@/routes/app/-components/use-authenticated-user"
import { useTranslation } from "react-i18next"
import { ChatPostDto } from "@/orval/gen/model"
import { useCreateChatPost } from "@/orval/gen"

export type CommentVisibility = "hidden" | "collapsed" | "visible"

interface Props {
  feedPost: ChatPostDto
  hideComments?: CommentVisibility
}

export function PostReactionsAndComments({ feedPost, hideComments }: Props) {
  const { t } = useTranslation("app_server")
  const [message, setMessage] = React.useState("")

  const { mutateAsync: sendMessage } = useCreateChatPost({
    mutation: {
      onSuccess: () => {
        setMessage("")
      },
    },
  })

  const handleSendMessage = async () => {
    await sendMessage({
      data: {
        chatId: feedPost.chatId,
        replyToMessageId: feedPost.chatPostId,
        message,
      },
    })
  }

  const textareaRef = React.useRef<HTMLTextAreaElement>(null)

  const currentUserId = useGetAuthenticatedUser().data.userId

  const renderInput = () => (
    <Group w="100%">
      <Textarea
        ref={textareaRef}
        flex="1"
        autosize
        placeholder={t("NewComment")}
        maxRows={15}
        style={{ pointerEvents: "all" }}
        value={message}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setMessage(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey && message.length) {
            e.preventDefault()
            handleSendMessage()
          }
        }}
      />
      <EmojiPickerDropdown
        focusTargetRef={textareaRef}
        actionIconProps={{
          variant: "subtle",
          style: {
            pointerEvents: "all",
          },
        }}
        position="right"
        onSelect={(emoji) => {
          setMessage((prev) => {
            return prev + emoji.emoji
          })
        }}
      />
      <ActionIcon
        style={{ pointerEvents: "all" }}
        disabled={!message.length}
        onClick={(e) => {
          e.stopPropagation()
          handleSendMessage()
        }}
      >
        <IconSend />
      </ActionIcon>
    </Group>
  )

  const LikeAndCommentGroup = React.memo(() => (
    <Group>
      <ReactionPillGroup
        messageId={feedPost.chatPostId}
        reactionCount={feedPost.reactions.length}
        userReacted={feedPost.reactions.some((id) => id === currentUserId)}
      />
      <Group gap="xs">
        <IconMessage2 />
        <Text>{feedPost.comments?.length}</Text>
      </Group>
    </Group>
  ))

  if (hideComments === "hidden") {
    return (
      <Card.Section p="sm">
        <LikeAndCommentGroup />
      </Card.Section>
    )
  }

  if (hideComments === "collapsed") {
    return (
      <Card.Section p="sm">
        <Accordion
          h="100%"
          variant="contained"
          styles={{
            label: { display: "flex", alignItems: "center" },
            content: { height: "100%" },
          }}
        >
          <Accordion.Item value="comments">
            <Accordion.Control
              component="div"
              onClick={(e) => e.stopPropagation()}
            >
              <LikeAndCommentGroup />
            </Accordion.Control>
            <Accordion.Panel onClick={(e) => e.stopPropagation()}>
              <Stack h="100%">
                <FeedPostCommentList mah="500px" topicPost={feedPost} />
                {renderInput()}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      </Card.Section>
    )
  }

  return (
    <Card.Section p="sm">
      <Stack>
        <Group>
          <ReactionPillGroup
            messageId={feedPost.chatPostId}
            reactionCount={feedPost.reactions.length}
            userReacted={feedPost.reactions.some((id) => id === currentUserId)}
          />
          <Group gap="xs">
            <IconMessage2 />
            <Text>{feedPost.comments?.length}</Text>
          </Group>
        </Group>
        {feedPost.comments?.length ? (
          <>
            <Divider />
            <Stack>
              <FeedPostCommentList topicPost={feedPost} />
              {renderInput()}
            </Stack>
          </>
        ) : (
          renderInput()
        )}
      </Stack>
    </Card.Section>
  )
}
